import { defineStore } from 'pinia'
import { apiGet, apiPost, ApiError } from '@/lib/api'
import type { Account, LoginPayload } from '@/types'

const SESSION_KEY = 'cintra.ib.session'

interface AuthState {
  account: Account | null
  loading: boolean
  error: string | null
  initialised: boolean
}

// Held at module scope so concurrent callers (router guard + App.onMounted)
// share the same in-flight network call instead of racing each other.
let restorePromise: Promise<void> | null = null

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    account: null,
    loading: false,
    error: null,
    initialised: false,
  }),
  getters: {
    isAuthenticated: (s) => !!s.account?.id,
    displayName: (s) => s.account?.full_name || 'Customer',
  },
  actions: {
    async login(payload: LoginPayload): Promise<boolean> {
      this.loading = true
      this.error = null
      try {
        const account = await apiPost<Account>('/auth/login.php', { ...payload })
        this.account = account
        this.initialised = true
        try {
          sessionStorage.setItem(SESSION_KEY, String(account.id))
        } catch {
          /* sessionStorage may be unavailable (private mode); ignore */
        }
        return true
      } catch (err) {
        this.error = err instanceof ApiError ? err.message : 'Login failed'
        return false
      } finally {
        this.loading = false
      }
    },

    async logout() {
      try {
        await apiPost('/auth/logout.php')
      } catch {
        /* swallow */
      }
      this.account = null
      this.initialised = true
      restorePromise = null
      try {
        sessionStorage.removeItem(SESSION_KEY)
      } catch {
        /* ignore */
      }
    },

    /**
     * Rehydrate the user from the server-side PHP session cookie.
     *
     * Must be awaited by the router guard on every refresh. Concurrent calls
     * share the same network request so the guard never sees a stale
     * `isAuthenticated === false` while `/auth/me.php` is still in flight.
     */
    async restoreSession(): Promise<void> {
      if (this.initialised) return
      if (restorePromise) return restorePromise

      restorePromise = (async () => {
        try {
          const account = await apiGet<Account | null>('/auth/me.php')
          if (account && account.id) {
            this.account = account
          } else {
            this.account = null
          }
        } catch {
          this.account = null
        } finally {
          this.initialised = true
        }
      })()

      try {
        await restorePromise
      } finally {
        restorePromise = null
      }
    },
  },
})
